import { getInvalidPlaceholders } from "./campaign-placeholders";

/**
 * The message-bearing fields of a campaign flow node that are checked for
 * invalid placeholder variables (the `alternate*` event variants are not).
 */
export interface CampaignNodeLike {
	data?: {
		message?: string;
		premiumMessage?: string;
		subject?: string;
	};
}

/**
 * Scans every node in every source (live edits + saved config) for placeholder
 * variables that are not in `validKeys`, and returns at most two human-readable
 * errors — one for message bodies, one for subjects — naming the offending
 * variables (PREC-3889).
 */
export const collectInvalidVariableErrors = (
	nodeSources: CampaignNodeLike[][],
	validKeys: string[]
): string[] => {
	const messageVars = new Set<string>();
	const subjectVars = new Set<string>();

	for (const nodes of nodeSources) {
		for (const node of nodes) {
			for (const content of [node.data?.message, node.data?.premiumMessage]) {
				if (content) {
					getInvalidPlaceholders(content, validKeys).forEach((varName) => messageVars.add(varName));
				}
			}

			if (node.data?.subject) {
				getInvalidPlaceholders(node.data.subject, validKeys).forEach((varName) =>
					subjectVars.add(varName)
				);
			}
		}
	}

	const formatVars = (vars: Set<string>) => [...vars].map((varName) => `{${varName}}`).join(", ");

	const errors: string[] = [];

	if (messageVars.size > 0) {
		errors.push(`Message contains invalid variables: ${formatVars(messageVars)}`);
	}

	if (subjectVars.size > 0) {
		errors.push(`Subject contains invalid variables: ${formatVars(subjectVars)}`);
	}

	return errors;
};
